import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';

const LeverancierCard = ({ leverancier }) => {
  const renderStars = (rating) => {
    const numRating = parseFloat(rating);
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <Icon
            key={star}
            name="Star"
            size={14}
            className={star <= numRating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  const hasActiveCertifications = leverancier?.provider_certifications?.some(
    cert => cert?.status === 'active'
  );

  const primaryServiceArea = leverancier?.provider_service_areas?.find(area => area?.is_primary) ||
                             leverancier?.provider_service_areas?.[0];

  return (
    <motion.div
      whileHover={{ 
        y: -12, 
        scale: 1.02,
        rotateX: 3,
        boxShadow: "0 20px 40px -10px rgba(0, 0, 0, 0.2), 0 0 12px rgba(59, 130, 246, 0.08)",
        transition: { duration: 0.3 } 
      }}
      className="bg-card border border-border rounded-xl p-6 shadow-[0_8px_24px_-4px_rgba(0,0,0,0.12)] hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.2)] transition-all h-full flex flex-col transform perspective-1000"
      style={{ transformStyle: 'preserve-3d' }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-heading font-bold text-foreground mb-2 line-clamp-2">
            {leverancier?.full_name || 'Aanbieder'}
          </h3>
          <div className="flex items-center space-x-2 mb-2">
            {renderStars(leverancier?.rating)}
            <span className="text-sm font-semibold text-foreground">{leverancier?.rating}</span>
            <span className="text-sm text-muted-foreground">({leverancier?.reviewCount})</span>
          </div>
        </div>
        {hasActiveCertifications && (
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="BadgeCheck" size={20} className="text-green-600" />
          </div>
        )}
      </div>
      {/* Specialties */}
      {leverancier?.specialties && leverancier?.specialties?.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-muted-foreground mb-2">Specialisaties</p>
          <div className="flex flex-wrap gap-2">
            {leverancier?.specialties?.map((specialty, idx) => (
              <span key={idx} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                {specialty}
              </span>
            ))}
          </div>
        </div>
      )}
      {/* Info */}
      <div className="space-y-2 mb-4 flex-1">
        {primaryServiceArea && (
          <div className="flex items-center space-x-2 text-sm">
            <Icon name="MapPin" size={16} className="text-muted-foreground flex-shrink-0" />
            <span className="text-foreground">
              {primaryServiceArea?.province || 'Landelijk'}
              {primaryServiceArea?.service_radius_km && (
                <span className="text-muted-foreground ml-1">
                  ({primaryServiceArea?.service_radius_km} km)
                </span>
              )}
            </span>
          </div>
        )}
        
        <div className="flex items-center space-x-2 text-sm">
          <Icon name="Clock" size={16} className="text-muted-foreground flex-shrink-0" />
          <span className="text-foreground">Reactietijd: {leverancier?.responseTime}</span>
        </div>
        
        {leverancier?.completedProjects > 0 && (
          <div className="flex items-center space-x-2 text-sm">
            <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0" />
            <span className="text-foreground">{leverancier?.completedProjects} projecten voltooid</span>
          </div>
        )}
      </div>
      {/* Certifications */}
      {hasActiveCertifications && (
        <div className="mb-4 pb-4 border-t border-border pt-4">
          <p className="text-xs text-muted-foreground mb-2">Certificeringen</p>
          <div className="flex flex-wrap gap-2">
            {leverancier?.provider_certifications
              ?.filter(cert => cert?.status === 'active')
              ?.slice(0, 3)
              ?.map((cert, idx) => (
                <div key={idx} className="flex items-center space-x-1 text-xs text-success">
                  <Icon name="Award" size={12} />
                  <span>{cert?.certification_name}</span>
                </div>
              ))}
          </div>
        </div>
      )}
      {/* Actions */}
      <div className="flex gap-2 mt-auto">
        <Link to={`/quote-request-form?leverancier=${leverancier?.id}`} className="flex-1">
          <Button size="sm" fullWidth iconName="MessageSquare" iconPosition="left">
            Offerte aanvragen
          </Button>
        </Link>
        <Link to={`/provider-profile/${leverancier?.id}`}>
          <Button
            variant="outline"
            size="sm"
            iconName="ExternalLink"
            className="flex-shrink-0"
            title="Bekijk profiel"
          />
        </Link>
      </div>
    </motion.div>
  );
};

export default LeverancierCard;